import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { HelpCircle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

const SCORE_LEGEND_VALUES = [
  { value: 0.82, labelKey: 'strong' as const, color: 'bg-emerald-500' },
  { value: 0.55, labelKey: 'moderate' as const, color: 'bg-amber-500' },
  { value: 0.31, labelKey: 'weak' as const, color: 'bg-slate-400' },
];

const KW_CHIP_CLASS = 'h-5 px-1.5 text-xs font-medium align-middle';

interface SearchHelpProps {
  /** i18n namespace that owns the `help.*` keys (e.g. 'person', 'projects'). */
  namespace: string;
  /**
   * Keyword pairs shown in the AND/OR examples. Defaults preserve the
   * keyword examples used on the People search help.
   */
  andExample?: [string, string];
  orExample?: [string, string];
}

export default function SearchHelp({
  namespace,
  andExample = ['machine learning', 'computer vision'],
  orExample = ['robotics', 'automation'],
}: SearchHelpProps) {
  const { t, i18n } = useTranslation(namespace);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }
      if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className="h-7 gap-1.5 px-2 text-xs font-normal text-ctu-text-muted hover:bg-muted/50 hover:text-ctu-text"
      >
        <HelpCircle className="h-3.5 w-3.5" />
        <span>{t('help.trigger')}</span>
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-[440px] overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle className="text-xl font-semibold tracking-tight">
              {t('help.title')}
            </SheetTitle>
            <SheetDescription>{t('help.subtitle')}</SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-8">
            <Section label={t('help.whatToSearch.label')}>
              <p className="text-sm leading-relaxed text-ctu-text">
                {t('help.whatToSearch.body')}
              </p>

              <SubSection
                label={t('help.whatToSearch.byName.label')}
                className="mt-5"
              >
                <p className="text-sm leading-relaxed text-ctu-text">
                  {t('help.whatToSearch.byName.body')}
                </p>
              </SubSection>

              <SubSection
                label={t('help.whatToSearch.byKeywords.label')}
                className="mt-5"
              >
                <p className="text-sm leading-relaxed text-ctu-text">
                  {t('help.whatToSearch.byKeywords.body')}
                </p>
                <div className="mt-3 space-y-2">
                  <ExampleRow
                    keywords={andExample}
                    op="AND"
                    result={t('help.whatToSearch.byKeywords.andResult')}
                  />
                  <ExampleRow
                    keywords={orExample}
                    op="OR"
                    result={t('help.whatToSearch.byKeywords.orResult')}
                  />
                </div>
              </SubSection>
            </Section>

            <Section label={t('help.engine.label')}>
              <div className="text-sm leading-relaxed text-ctu-text">
                <Trans
                  i18nKey="help.engine.body"
                  ns={namespace}
                  components={{
                    kw: <Badge variant="info" className={KW_CHIP_CLASS} />,
                  }}
                />
              </div>
            </Section>

            <Section label={t('help.readingResults.label')}>
              <p className="text-sm leading-relaxed text-ctu-text">
                {t('help.readingResults.body')}
              </p>
              <div className="mt-3 space-y-1.5">
                {SCORE_LEGEND_VALUES.map((row) => (
                  <div
                    key={row.labelKey}
                    className="flex items-center gap-3 text-xs"
                  >
                    <span className="w-10 font-mono tabular-nums text-ctu-text">
                      {row.value.toFixed(2)}
                    </span>
                    <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                      <div
                        className={`h-full rounded-full ${row.color}`}
                        style={{ width: `${row.value * 100}%` }}
                      />
                    </div>
                    <span className="text-ctu-text-muted">
                      {t(`help.readingResults.${row.labelKey}`)}
                    </span>
                  </div>
                ))}
              </div>
              {i18n.exists('help.readingResults.tieOrder', { ns: namespace }) && (
                <p className="mt-4 text-sm leading-relaxed text-ctu-text-muted">
                  {t('help.readingResults.tieOrder')}
                </p>
              )}
            </Section>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="mb-3 text-base font-semibold tracking-tight text-ctu-text">
        {label}
      </h3>
      {children}
    </section>
  );
}

function SubSection({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <h4 className="mb-2 text-sm font-semibold text-ctu-text">{label}</h4>
      {children}
    </div>
  );
}

function ExampleRow({
  keywords,
  op,
  result,
}: {
  keywords: readonly [string, string];
  op: 'AND' | 'OR';
  result: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      <Search className="h-3.5 w-3.5 shrink-0 text-ctu-text-muted" aria-hidden />
      {keywords.map((kw, i) => (
        <span key={`${op}-${i}`} className="inline-flex items-center gap-1.5">
          {i > 0 && (
            <Badge
              variant="outline"
              className="h-5 rounded px-1.5 text-[10px] font-semibold uppercase tracking-wide text-ctu-text-muted"
            >
              {op}
            </Badge>
          )}
          <Badge variant="info" className={KW_CHIP_CLASS}>
            {kw}
          </Badge>
        </span>
      ))}
      <span className="ml-auto text-ctu-text-muted">→ {result}</span>
    </div>
  );
}
